import { computed, inject, Injectable, signal, Signal, WritableSignal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { CustomerModel } from '@libs/account/models';
import { FlashMessageService, SpinnerOverlayService } from '@libs/shared/common';
import { AuthToken } from '@libs/shared/models';
import { catchError, defer, finalize, firstValueFrom, from, map, Observable, tap } from 'rxjs';
import { UserAuthStateModel } from './auth.model';
import { NoPermissionNotificationService } from './no-permission-notification';
import { AuthState, NoPermittedCustomersError } from './state';

/* eslint-disable @typescript-eslint/member-ordering */

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly authState: AuthState = inject(AuthState);
  private readonly spinnerOverlayService: SpinnerOverlayService = inject(SpinnerOverlayService);
  private readonly flashMessageService: FlashMessageService = inject(FlashMessageService);
  private readonly noPermissionNotification: NoPermissionNotificationService = inject(NoPermissionNotificationService);
  private readonly router: Router = inject(Router);
  private readonly activatedRoute: ActivatedRoute | null = inject(ActivatedRoute, { optional: true });
  private readonly _isUpdating: WritableSignal<boolean> = signal(false);

  public isCurrentCustomerBlocked: Signal<boolean> = this.authState.isCurrentCustomerBlocked;
  public currentCustomer: Signal<CustomerModel | undefined> = this.authState.currentCustomer;
  public allCustomers: Signal<CustomerModel[] | undefined> = this.authState.allCustomers;
  public isUpdating: Signal<boolean> = computed(() => {
    return this._isUpdating() || this.authState.isLoading();
  });

  public setIsUpdating(isUpdating: boolean): void {
    this._isUpdating.set(isUpdating);
  }

  public isUserAuthorized$(type: keyof UserAuthStateModel = 'user'): Observable<boolean> {
    return this.authState.readUserAuthState().pipe(map((model) => {
      return model[type];
    }));
  }

  public agentSetUser(userId: string): Observable<void> {
    return defer(() => {
      this.spinnerOverlayService.showLoader();

      return from(this.authState.agentSetUser(userId)).pipe(
        tap(() => void this.onLoginFinished()),
        catchError(() => this.onLoginFailed()),
        finalize(() => void this.spinnerOverlayService.hideLoader()),
      );
    });
  }

  public login(newToken?: AuthToken): Observable<void> {
    return this.authenticate(() => this.authState.login(newToken));
  }

  /**
   * Starts the sign-up flow. Web: redirects to the azure sign-up page, the login completes
   * on return. Native: opens the sign-up policy in the MSAL browser and logs in with the result.
   */
  public register(): Observable<void> {
    return this.authenticate(() => this.authState.register());
  }

  public loginLlDirectoryUser(newToken: AuthToken): Observable<void> {
    return defer(() => {
      this.spinnerOverlayService.showLoader();
      this._isUpdating.set(true);

      return from(this.authState.loginLlDirectoryUser(newToken)).pipe(
        tap(() => {
          this.flashMessageService.showMessage('successLogin');
          void this.router.navigateByUrl('/agent');
        }),
        catchError(() => this.onLoginFailed()),
        finalize(() => {
          this._isUpdating.set(false);
          void this.spinnerOverlayService.hideLoader();
        }),
      );
    });
  }

  public loginCustomerByNumber(customerNumber: string): Promise<void> {
    return this.authState.loginSingleCustomer(customerNumber);
  }

  public logout(): Observable<void> {
    return defer(() => {
      this.spinnerOverlayService.showLoader();

      return from(this.authState.logout()).pipe(
        finalize(() => {
          this.spinnerOverlayService.hideLoader();
        }),
      );
    });
  }

  public loginUser(newToken?: AuthToken): Observable<void> {
    return this.authState.loginUser(newToken);
  }

  public checkAuthState(): Promise<void> {
    this.spinnerOverlayService.showLoader();

    return this.authState.checkAuthState()
      .then((result) => {
        if (result === 'success') {
          setTimeout(() => {
            if (this.router.url.includes('auth')) {
              void this.handleRedirectAfterLogin();
            }
          });
        } else if (result === 'agentNoUserId') {
          void this.router.navigateByUrl('/agent');
        }
      }).catch((err: unknown) => {
        console.error(err);
        void this.onLoginFailed(err);
      }).finally(() => {
        this.spinnerOverlayService.hideLoader();
      });
  }

  public getAllCustomers(): Observable<CustomerModel[] | undefined> {
    return this.authState.getAllCustomers();
  }

  public customersLoaded(): boolean {
    return this.authState.customersLoaded();
  }

  public async changeCustomer(customer: CustomerModel): Promise<void> {
    return this.authState.changeCurrentCustomer(customer);
  }

  public getCurrentCustomer(): Observable<CustomerModel | undefined> {
    return this.authState.getCurrentCustomer();
  }

  public async loadCustomers(): Promise<void> {
    return this.authState.loadCustomers();
  }

  public clearCustomers(): void {
    return void this.authState.clearCustomers();
  }

  // only for use in storybook.
  public setAuthRaw(authToken: AuthToken, customers: CustomerModel[]): Promise<void> {
    return this.authState.setAuthRaw(authToken, customers);
  }

  public onLoginFailed(error?: unknown): Promise<void> {
    if (error instanceof NoPermittedCustomersError) {
      // Keep the no-permission flag until the app starts again
      this.noPermissionNotification.markPending();
    } else {
      this.flashMessageService.showMessage('errorLogin');
    }

    return firstValueFrom(this.logout())
      .finally(() => void this._isUpdating.set(false));
  }

  private authenticate(action: () => Promise<void>): Observable<void> {
    return defer(() => {
      this.spinnerOverlayService.showLoader();
      this._isUpdating.set(true);

      return from(action()).pipe(
        tap(() => void this.onLoginFinished()),
        catchError(error => this.onLoginFailed(error)),
        finalize(() => {
          this._isUpdating.set(false);
          void this.spinnerOverlayService.hideLoader();
        }),
      );
    });
  }

  private onLoginFinished(): Promise<boolean> {
    this.flashMessageService.showMessage('successLogin');

    return this.handleRedirectAfterLogin();
  }

  private handleRedirectAfterLogin(): Promise<boolean> {
    const redirectUrl: string | undefined = (this.activatedRoute?.snapshot.queryParams['returnUrl'] as unknown)?.toString();

    if (redirectUrl) {
      return this.router.navigateByUrl(redirectUrl);
    } else {
      return this.router.navigateByUrl('');
    }
  }
}
